import React, { Component } from 'react'
import { observer, inject } from 'mobx-react'
import { tranzila } from '@services/api'
import subscriptionService from '@services/subscriptions'
import { Row, Col, Button, Form, Icon, Modal, Radio, Tooltip, notification } from 'antd'
import {
    xIcon,
    formMain,
    formInner,
    formTitle,
    formPadding,
    refreshFormIcon,
    columnTitle
} from '../EditSubStoDataPopup/edit-sub-sto.scss'
import ClientInfo from './ClientInfo'
import PaymentInfo from './PaymentInfo'
import PaymentMethod from './PaymentMethod'
import SearchUserModal from '@components/Popup/CreateBankSto/SearchUserModal'
import SearchSubModal from '@components/Popup/CreateBankSto/SearchSubModal'
import TransactionProgressModal from './TransactionProgressModal'
import { FormContext } from './FormContext'
import { localCodes } from '@utils/index'
import { stoFailureText } from '@pages/Tranzila/stoErrors'
import moment from 'moment'

const { Item: FormItem } = Form

// ── constants ────────────────────────────────────────────────
const signCurrency = {
    USD: '$',
    EUR: '€',
    ILS: '₪'
}

const DEFAULT_TERMINAL_TYPE = 'mediwho1tok'

const CLIENT_FORM_FIELDS = [
    'name',
    'id',
    'address',
    'email',
    'phone_country_code',
    'phone_area_code',
    'phone_number',
    'client_id',
    'client_full_name',
    'user_sub_id'
]

const ITEM_FORM_FIELDS = [
    'unit_price',
    'units_number',
    'code',
    'name',
    'type',
    'price_type',
    'internal_id',
    'vat_percent'
]
// (החל מחודש שני)
const SETUP_FEE_ITEM = { name: 'חיוב הקמת הרשאה', sku: 'H-0010', price: 18 }
const CLEARING_FEE_ITEM = { name: 'דמי סליקה חודשיים', sku: 'H-0015', price: 3 }
const EXTRA_CHARGE_SKUS = ['H-0010', 'H-0015']

const getClearingFeeItemName = (debitType?: string, firstMonthCharge?: string, recurringCharge?: string) =>
    debitType === 'immediate_and_recurring' && recurringCharge === 'clearing_fee' && firstMonthCharge !== 'clearing_fee'
        ? `${CLEARING_FEE_ITEM.name} (החל מחודש שני)`
        : CLEARING_FEE_ITEM.name

const initialState = {
    loading: false,
    items: [],
    currencySign: '₪',
    searchVisible: true,
    existActiveOrder: false,
    subModalVisible: false,
    subItemIndex: 0,
    progressSteps: [] as any[],
    progressVisible: false,
    progressError: '',
    progressClosable: false,
    retriedSteps: new Set() as Set<number>,
    documentUrl: null as string | null
}

// ── notification / modal helpers ─────────────────────────────
const notificationBaseStyle: React.CSSProperties = {
    direction: 'rtl',
    textAlign: 'right',
    borderRadius: 4,
    paddingRight: 48,
    paddingLeft: 24
}

const notifyWarning = msg => {
    notification.warning({
        message: msg,
        duration: 3,
        style: {
            ...notificationBaseStyle,
            backgroundColor: '#fffbe6',
            border: '1px solid #ffe58f'
        }
    })
}

// ── main component ───────────────────────────────────────────
@inject((store: any) => {
    const { authStore } = store
    return { authStore }
})
@observer
class CreateBankSto extends Component<any, any> {
    _nextItemId = 0
    _submissionInProgress = false
    _retryPayload: any = null
    _retryVerifyData: any = null
    _retryDebitType: string = ''
    _retryStoItems: any = null
    _retryChargeItems: any = null
    _retryStoData: any = null
    _retryChargeData: any = null
    _retryDocumentUrl: string | null = null
    _retryDebitData: any = null
    _foundUserSub: any = null
    _activationAttempted = false
    _orphanCleanupAttempted = false
    _orphanStoLive = false
    _docTimers: any[] = []
    _docPollingActive = false

    constructor(props) {
        super(props)
        this.state = { ...initialState, terminalType: DEFAULT_TERMINAL_TYPE }
    }

    componentDidMount() {
        this.applyDateDefaults()
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.visible && this.props.visible) {
            setTimeout(() => this.applyDateDefaults(), 0)
        }
    }

    componentWillUnmount() {
        this.clearDocTimers()
    }

    // ── state helpers ────────────────────────────────────────
    getDefaultChargeDay = () => Math.min(moment().date(), 28)

    // default start = same day-of-month, NEXT month, time zeroed
    getDefaultChargeDate = () =>
        moment()
            .add(1, 'month')
            .date(this.getDefaultChargeDay())
            .startOf('day')

    // day → date: keep the current month/year, swap the day; roll forward if that lands in the past
    dateFromDay = (day, currentDate) => {
        const base = currentDate && currentDate.isValid() ? currentDate.clone() : moment().add(1, 'month')
        let next = base.date(Math.min(Number(day) || 1, 28)).startOf('day')
        if (!next.isAfter(moment().endOf('day'))) next = next.add(1, 'month')
        return next
    }

    applyDateDefaults = () => {
        this.props.form.setFieldsValue({
            charge_dom: this.getDefaultChargeDay(),
            first_charge_date: this.getDefaultChargeDate()
        })
    }

    clearDocTimers = () => {
        this._docTimers.forEach(id => clearInterval(id))
        this._docTimers = []
        this._docPollingActive = false
    }

    resetAll = () => {
        if (this._submissionInProgress || this.state.loading) return

        this.clearDocTimers()
        this._retryPayload = null
        this._retryVerifyData = null
        this._retryDebitType = ''
        this._retryStoItems = null
        this._retryChargeItems = null
        this._retryStoData = null
        this._retryChargeData = null
        this._retryDocumentUrl = null
        this._retryDebitData = null
        this._foundUserSub = null
        this._activationAttempted = false
        this._orphanCleanupAttempted = false
        this._orphanStoLive = false
        this.setState({ ...initialState, terminalType: DEFAULT_TERMINAL_TYPE })
        this.props.form.resetFields()
        this.applyDateDefaults()
    }

    setLoading = (loading: boolean) => this.setState({ loading })

    getDocumentUrl = (retrievalKey?: string | null): string | undefined => {
        if (!retrievalKey) return undefined
        return `https://my.tranzila.com/api/get_financial_document/${retrievalKey}`
    }

    startDocumentPolling = () => {
        const payload = this._retryPayload
        if (!payload) return
        const stepIndex = this.state.progressSteps.findIndex(s => s.isDoc)
        if (stepIndex === -1) return
        this.clearDocTimers()
        this._docPollingActive = true

        const runFetch = async (attempt: number) => {
            if (!this._docPollingActive) return
            try {
                const { data } = await tranzila.stoBankDocument({ ...payload })
                if (!this._docPollingActive) return
                if (data && data.retrieval_key) {
                    this._retryDocumentUrl = this.getDocumentUrl(data.retrieval_key)
                    this.updateStep(stepIndex, 'success')
                    this.setState({ documentUrl: this._retryDocumentUrl })
                    this._docPollingActive = false
                    return
                }
            } catch (e) {
                if (!this._docPollingActive) return
                console.log('document polling fetch err::=>', e)
            }
            if (attempt >= 3) {
                if (!this._docPollingActive) return
                this.setState(prev => ({
                    progressSteps: prev.progressSteps.map(s =>
                        s.isDoc
                            ? { ...s, status: 'warning', label: 'המסמך לא נמצא, ניתן להפיק מעמוד הלקוח' }
                            : s
                    )
                }))
                this._docPollingActive = false
                return
            }
            startCountdown(10, `מנסה להפיק חשבונית... (${attempt + 1}/3)`, () => runFetch(attempt + 1))
        }

        const startCountdown = (seconds: number, labelPrefix: string, onDone: () => void) => {
            if (!this._docPollingActive) return
            let remaining = seconds
            const setLabel = (sec: number) => {
                if (!this._docPollingActive) return
                this.setState(prev => ({
                    progressSteps: prev.progressSteps.map(s =>
                        s.isDoc
                            ? {
                                  ...s,
                                  label: labelPrefix
                                      ? `${labelPrefix} — ${sec}ש׳`
                                      : `החשבונית תופק בעוד ${sec}ש׳`
                              }
                            : s
                    )
                }))
            }
            setLabel(remaining)
            const id = setInterval(() => {
                if (!this._docPollingActive) {
                    clearInterval(id)
                    return
                }
                remaining -= 1
                if (remaining <= 0) {
                    clearInterval(id)
                    this._docTimers = this._docTimers.filter(t => t !== id)
                    onDone()
                    return
                }
                setLabel(remaining)
            }, 1000)
            this._docTimers.push(id)
        }

        this.updateStep(stepIndex, 'loading')
        startCountdown(60, '', () => runFetch(0))
    }

    // ── form submit ──────────────────────────────────────────
    scrollToFirstError = err => {
        if (!err) return
        const firstField = Object.keys(err)[0]
        if (!firstField) return

        const node = document.getElementById(firstField) || document.querySelector(`[id="${firstField}"]`)
        if (!node) return

        node.scrollIntoView({ behavior: 'smooth', block: 'center' })
        setTimeout(() => {
            if (typeof node.focus === 'function') node.focus()
        }, 300)
    }

    handleSubmit = e => {
        e.preventDefault()
        // Server enforces admin/money too; this blocks the Enter-key bypass of the disabled button.
        const { roles } = this.props.authStore
        if (!roles.includes('admin') && !roles.includes('money')) return
        if (this._submissionInProgress || this.state.loading) return

        this._submissionInProgress = true
        if (this.state.terminalType === 'mediwhobk') {
            this.handleBankSubmit()
        } else {
            this.handleCreditSubmit()
        }
    }

    handleBankSubmit = () => {
        this.props.form.validateFields(async (err, values) => {
            if (err) {
                this._submissionInProgress = false
                this.scrollToFirstError(err)
                return
            }

            const { valid, message } = this.validateItems()
            if (!valid) {
                this._submissionInProgress = false
                notifyWarning(message)
                return
            }

            const debitType = values.debit_type
            const steps = this.getProgressSteps(debitType, true)
            if (this._foundUserSub && this._foundUserSub.user_sub_status === 'AWAITING_BANK_APPROVAL') {
                steps.push({ label: 'הפעלת מנוי', status: 'pending' })
            }
            if (debitType === 'immediate_and_recurring') {
                steps.push({ label: 'הפקת חשבונית...', status: 'pending', canRetry: false, isDoc: true })
            }
            const payload = {
                ...values,
                terminal_name: this.state.terminalType,
                first_charge_date: values.first_charge_date
                    ? values.first_charge_date.format('YYYY-MM-DD')
                    : undefined
            }

            const { stoItems, chargeItems } = this.prepareBankItems(payload.items, debitType, values)

            this._retryPayload = payload
            this._retryDebitType = debitType
            this._retryStoItems = stoItems
            this._retryChargeItems = chargeItems
            this._retryStoData = null
            this._retryChargeData = null

            this.setState({
                loading: true,
                progressVisible: true,
                progressSteps: steps,
                progressError: '',
                progressClosable: false,
                retriedSteps: new Set()
            })

            try {
                if (debitType === 'recurring_only') {
                    this.updateStep(0, 'loading')
                    const { data: stoData } = await tranzila.stoCreate({ ...payload, items: stoItems })
                    this._retryStoData = stoData
                    this.updateStep(0, 'success')

                    await this.runAutoActivation()
                    this.setState({ progressClosable: true, loading: false })
                    return
                }

                if (debitType === 'immediate_and_recurring') {
                    this.updateStep(0, 'loading')
                    const { data: stoData } = await tranzila.stoCreate({ ...payload, items: stoItems })
                    this._retryStoData = stoData
                    this.updateStep(0, 'success')

                    this.updateStep(1, 'loading')
                    const { data: chargeData } = await tranzila.stoBankCharge({
                        ...payload,
                        charge_items: chargeItems
                    })
                    this._retryChargeData = chargeData
                    this.updateStep(1, 'success')

                    await this.runAutoActivation()
                    this.setState({ progressClosable: true, loading: false })
                    this.startDocumentPolling()
                    return
                }
            } catch (error) {
                const res = error.response
                const errMsg =
                    (res && res.data && (res.data.err_message || res.data.details || res.data.message)) ||
                    'שגיאה בתהליך'
                // MASAV bank-detail codes (701-704): retrying with the same details reproduces them.
                const isBankDetailError = !!(res && res.data && res.data.err_key === 'BANK_DETAILS_INVALID')

                this.setState(prev => ({
                    progressSteps: prev.progressSteps.map(s =>
                        s.status === 'loading'
                            ? {
                                  ...s,
                                  status: 'error',
                                  canRetry: isBankDetailError ? false : !prev.retriedSteps.has(s.index)
                              }
                            : s
                    ),
                    progressError: errMsg,
                    progressClosable: true,
                    loading: false
                }))
                if (isBankDetailError && this._retryStoData && this._retryStoData.sto_id) {
                    this.deactivateOrphanSto()
                }
            } finally {
                this._submissionInProgress = false
            }
        })
    }

    getProgressSteps = (debitType: string, isBank: boolean = false) => {
        if (isBank) {
            switch (debitType) {
                case 'recurring_only':
                    return [{ label: 'יצירת הוראת חיוב בנקאית...', status: 'pending', index: 0, canRetry: true }]
                case 'immediate_and_recurring':
                    return [
                        { label: 'יצירת הוראת חיוב בנקאית...', status: 'pending', index: 0, canRetry: true },
                        { label: 'ביצוע חיוב מס״ב...', status: 'pending', index: 1, canRetry: true }
                    ]
                default:
                    return [{ label: 'מעבד...', status: 'pending', index: 0, canRetry: true }]
            }
        }

        switch (debitType) {
            case 'immediate_only':
                return [{ label: 'ביצוע חיוב...', status: 'pending', index: 0, canRetry: true }]
            case 'recurring_only':
                return [
                    { label: 'אימות כרטיס אשראי...', status: 'pending', index: 0, canRetry: true },
                    { label: 'יצירת הוראת חיוב...', status: 'pending', index: 1, canRetry: true }
                ]
            case 'immediate_and_recurring':
                return [
                    { label: 'ביצוע חיוב מיידי...', status: 'pending', index: 0, canRetry: true },
                    { label: 'יצירת הוראת חיוב...', status: 'pending', index: 1, canRetry: true }
                ]
            default:
                return [{ label: 'מעבד...', status: 'pending', index: 0, canRetry: true }]
        }
    }

    prepareItemsForVerify = (items: any[]) => {
        if (!items || !Array.isArray(items)) return items
        return items.map(item => ({ ...item, unit_price: 1 }))
    }

    prepareItemsForSto = (items: any[], vatPercent: number = 18) => {
        if (!items || !Array.isArray(items)) return items
        const vatMultiplier = 1 + vatPercent / 100
        return items.map(item => {
            const { vat_percent, ...rest } = item
            return {
                ...rest,
                price_type: 'N',
                unit_price: item.unit_price ? parseFloat((item.unit_price / vatMultiplier).toFixed(4)) : item.unit_price
            }
        })
    }

    prepareBankItems = (formItems: any[], debitType: string, values: any) => {
        const { first_month_charge, recurring_charge } = values
        const allItems = formItems ? [...formItems] : []
        const mainItems = allItems.filter(item => item.code !== 'H-0010' && item.code !== 'H-0015')

        if (debitType === 'recurring_only') {
            return { stoItems: allItems, chargeItems: [] }
        }

        // immediate_and_recurring
        const stoItems = [...mainItems]
        if (recurring_charge === 'clearing_fee') {
            stoItems.push({
                unit_price: 3,
                units_number: 1,
                code: 'H-0015',
                name: getClearingFeeItemName(debitType, first_month_charge, recurring_charge),
                type: 'I',
                price_type: 'G'
            })
        }

        const chargeItems = [...mainItems]
        if (first_month_charge === 'setup_fee') {
            chargeItems.push({
                unit_price: 18,
                units_number: 1,
                code: 'H-0010',
                name: 'חיוב הקמת הרשאה',
                type: 'I',
                price_type: 'G'
            })
        } else if (first_month_charge === 'clearing_fee') {
            chargeItems.push({
                unit_price: 3,
                units_number: 1,
                code: 'H-0015',
                name: CLEARING_FEE_ITEM.name,
                type: 'I',
                price_type: 'G'
            })
        }

        return { stoItems, chargeItems }
    }

    updateStep = (index: number, status: 'loading' | 'success' | 'error') => {
        this.setState(prev => {
            const steps = [...prev.progressSteps]
            if (steps[index]) steps[index] = { ...steps[index], status }
            return { progressSteps: steps }
        })
    }

    runAutoActivation = async () => {
        const ctx = this._foundUserSub
        // Only when: we have a found sub, it awaits bank approval, and we haven't tried yet.
        if (!ctx || !ctx.user_sub_id || !ctx.client_id) return
        if (ctx.user_sub_status !== 'AWAITING_BANK_APPROVAL') return
        if (this._activationAttempted) return

        const stepIndex = this.state.progressSteps.findIndex(s => (s.label || '').startsWith('הפעלת מנוי'))
        if (stepIndex === -1) return
        this._activationAttempted = true

        this.updateStep(stepIndex, 'loading')
        try {
            const payload: any = {
                user_id: ctx.client_id,
                user_subscription_id: ctx.user_sub_id
            }
            // MONTHS coupon only — mirrors SubscriptionType.couponDetails / monthsCoupon.
            if (ctx.coupon_details && ctx.coupon_details.units === 'MONTHS') {
                const today = new Date()
                today.setMonth(today.getMonth() + ctx.coupon_details.discount)
                payload.coupon_details = {
                    discount: ctx.coupon_details.discount,
                    future_date: today.toISOString().split('T')[0]
                }
            }
            await subscriptionService.updateBankApprovalStatus(payload)
            this.updateStep(stepIndex, 'success')
        } catch (error) {
            console.log('auto activation err::=>', error)
            // ONE attempt only — no retry button. Manual fallback exists on the client card.
            this.setState(prev => ({
                progressSteps: prev.progressSteps.map((s, i) =>
                    i === stepIndex
                        ? { ...s, status: 'error', canRetry: false, label: 'הפעלת מנוי — ניתן להפעיל ידנית מעמוד הלקוח' }
                        : s
                )
            }))
        }
    }

    // The STO is created before the MASAV charge. A bank-detail rejection leaves it LIVE
    // in Tranzila charging monthly — roll it back and say so loudly.
    deactivateOrphanSto = async () => {
        const stoData = this._retryStoData
        const payload = this._retryPayload
        if (!stoData || !stoData.sto_id || !payload) return
        if (this._orphanCleanupAttempted) return
        this._orphanCleanupAttempted = true
        this._orphanStoLive = true // an active orphan exists until proven otherwise

        // Drop steps that will never execute now (activation / document), then append ours.
        this.setState(prev => ({
            progressSteps: [
                ...prev.progressSteps.filter(s => s.status !== 'pending'),
                { label: 'כיבוי הוראת הקבע...', status: 'loading', canRetry: false, isCleanup: true }
            ]
        }))

        try {
            await tranzila.updateStoStatus({
                sto_id: stoData.sto_id,
                terminal_name: payload.terminal_name,
                user_sub_id: payload.user_sub_id,
                update_status: 'inactive',
                clear_linkage: true
            })
            this._orphanStoLive = false
            this.setState(prev => ({
                progressSteps: prev.progressSteps.map(s =>
                    s.isCleanup
                        ? { ...s, status: 'warning', label: 'הוראת הקבע כובתה — יש לתקן את פרטי הבנק ולנסות שוב' }
                        : s
                )
            }))
        } catch (e) {
            console.log('orphan sto deactivation err::=>', e)
            this.setState(prev => ({
                progressSteps: prev.progressSteps.map(s =>
                    s.isCleanup
                        ? {
                              ...s,
                              status: 'error',
                              canRetry: false,
                              label: 'כיבוי הוראת הקבע נכשל — חובה לכבות ידנית בעמוד ניהול הוראות קבע'
                          }
                        : s
                ),
                progressError: 'הוראת הקבע פעילה ותחייב את הלקוח — יש לכבותה ידנית לפני יצירת הוראה חדשה'
            }))
        }
    }

    handleCreditSubmit = () => {
        this.props.form.validateFields(async (err, values) => {
            if (err) {
                this._submissionInProgress = false
                this.scrollToFirstError(err)
                return
            }

            const { valid, message } = this.validateItems()
            if (!valid) {
                this._submissionInProgress = false
                notifyWarning(message)
                return
            }

            const debitType = values.debit_type
            const steps = this.getProgressSteps(debitType)
            if (this._foundUserSub && this._foundUserSub.user_sub_status === 'AWAITING_BANK_APPROVAL') {
                steps.push({ label: 'הפעלת מנוי', status: 'pending' })
            }
            if (debitType === 'immediate_only' || debitType === 'immediate_and_recurring') {
                steps.push({ label: 'הפקת חשבונית...', status: 'pending', canRetry: false, isDoc: true })
            }
            const payload = {
                ...values,
                terminal_name: this.state.terminalType,
                first_charge_date: values.first_charge_date
                    ? values.first_charge_date.format('YYYY-MM-DD')
                    : undefined
            }
            if (payload.card_number) {
                payload.card_number = payload.card_number.replace(/\s/g, '')
            }

            this._retryPayload = payload
            this._retryDebitType = debitType
            this._retryVerifyData = null
            this._retryStoItems = null
            this._retryChargeItems = null
            this._retryStoData = null
            this._retryChargeData = null

            this.setState({
                loading: true,
                progressVisible: true,
                progressSteps: steps,
                progressError: '',
                progressClosable: false,
                retriedSteps: new Set()
            })

            try {
                if (debitType === 'immediate_only') {
                    this.updateStep(0, 'loading')
                    await tranzila.stoDebit({
                        ...payload,
                        items: this.prepareItemsForSto(payload.items, payload.vat_percent)
                    })
                    this.updateStep(0, 'success')

                    await this.runAutoActivation()
                    this.setState({ progressClosable: true, loading: false })
                    this.startDocumentPolling()
                    return
                }

                if (debitType === 'recurring_only') {
                    this.updateStep(0, 'loading')
                    const { data: verifyData } = await tranzila.stoVerify({
                        ...payload,
                        verify_mode: 2,
                        items: this.prepareItemsForVerify(payload.items)
                    })
                    this._retryVerifyData = verifyData
                    this.updateStep(0, 'success')

                    this.updateStep(1, 'loading')
                    await tranzila.stoCreate({ ...payload, token: verifyData.token })
                    this.updateStep(1, 'success')

                    await this.runAutoActivation()
                    this.setState({ progressClosable: true, loading: false })
                    return
                }

                if (debitType === 'immediate_and_recurring') {
                    // Step 1: debit with mediwho1 terminal → get token
                    this.updateStep(0, 'loading')
                    const { data: debitData } = await tranzila.stoDebit({
                        ...payload,
                        terminal_name: 'mediwho1',
                        items: this.prepareItemsForSto(payload.items, payload.vat_percent)
                    })
                    this._retryDebitData = debitData
                    this.updateStep(0, 'success')

                    // Step 2: create STO with token from debit → mediwho1tok
                    this.updateStep(1, 'loading')
                    await tranzila.stoCreate({
                        ...payload,
                        terminal_name: 'mediwho1tok',
                        token: debitData.token
                    })
                    this.updateStep(1, 'success')

                    await this.runAutoActivation()
                    this.setState({ progressClosable: true, loading: false })
                    this.startDocumentPolling()
                    return
                }
            } catch (error) {
                const res = error.response
                const errMsg =
                    (res && res.data && (res.data.err_message || res.data.details || res.data.message)) ||
                    'שגיאה בתהליך'

                this.setState(prev => ({
                    progressSteps: prev.progressSteps.map(s =>
                        s.status === 'loading'
                            ? { ...s, status: 'error', canRetry: !prev.retriedSteps.has(s.index) }
                            : s
                    ),
                    progressError: errMsg,
                    progressClosable: true,
                    loading: false
                }))
            } finally {
                this._submissionInProgress = false
            }
        })
    }

    handleRetryStep = async (stepIndex: number) => {
        const payload = this._retryPayload
        const debitType = this._retryDebitType
        const verifyData = this._retryVerifyData

        if (!payload || this._submissionInProgress || this.state.loading) return
        this._submissionInProgress = true

        this.setState(prev => {
            const retriedSteps = new Set(prev.retriedSteps)
            retriedSteps.add(stepIndex)
            const steps = [...prev.progressSteps]
            if (steps[stepIndex]) {
                steps[stepIndex] = { ...steps[stepIndex], status: 'loading', canRetry: false }
            }
            return { progressSteps: steps, retriedSteps, progressError: '', progressClosable: false, loading: true }
        })

        try {
            // ── BANK MODE ───────────────────────────────────────────────
            if (this.state.terminalType === 'mediwhobk') {
                const stoItems = this._retryStoItems
                const chargeItems = this._retryChargeItems

                if (debitType === 'recurring_only') {
                    if (stepIndex === 0) {
                        const { data: stoData } = await tranzila.stoCreate({ ...payload, items: stoItems })
                        this._retryStoData = stoData
                        this.updateStep(0, 'success')
                        await this.runAutoActivation()
                        this.setState({ progressClosable: true, loading: false })
                        return
                    }
                }

                if (debitType === 'immediate_and_recurring') {
                    if (stepIndex === 0) {
                        const { data: stoData } = await tranzila.stoCreate({ ...payload, items: stoItems })
                        this._retryStoData = stoData
                        this.updateStep(0, 'success')

                        this.updateStep(1, 'loading')
                        const { data: chargeData } = await tranzila.stoBankCharge({
                            ...payload,
                            charge_items: chargeItems
                        })
                        this._retryChargeData = chargeData
                        this.updateStep(1, 'success')

                        await this.runAutoActivation()
                        this.setState({ progressClosable: true, loading: false })
                        this.startDocumentPolling()
                        return
                    }

                    if (stepIndex === 1) {
                        const { data: chargeData } = await tranzila.stoBankCharge({
                            ...payload,
                            charge_items: chargeItems
                        })
                        this._retryChargeData = chargeData
                        this.updateStep(1, 'success')

                        await this.runAutoActivation()
                        this.setState({ progressClosable: true, loading: false })
                        this.startDocumentPolling()
                        return
                    }
                }

                // Bank mode handled above exclusively — never fall through into the
                // credit branches below (they key on debitType and expect verify/debit data).
                return
            }

            // ── IMMEDIATE_ONLY ──────────────────────────────────────────
            if (debitType === 'immediate_only') {
                if (stepIndex === 0) {
                    await tranzila.stoDebit({
                        ...payload,
                        items: this.prepareItemsForSto(payload.items, payload.vat_percent)
                    })
                    this.updateStep(0, 'success')

                    await this.runAutoActivation()
                    this.setState({ progressClosable: true, loading: false })
                    this.startDocumentPolling()
                    return
                }
            }

            // ── RECURRING_ONLY ──────────────────────────────────────────
            if (debitType === 'recurring_only') {
                if (stepIndex === 0) {
                    const { data: newVerifyData } = await tranzila.stoVerify({
                        ...payload,
                        verify_mode: 2,
                        items: this.prepareItemsForVerify(payload.items)
                    })
                    this._retryVerifyData = newVerifyData
                    this.updateStep(0, 'success')

                    this.updateStep(1, 'loading')
                    await tranzila.stoCreate({ ...payload, token: newVerifyData.token })
                    this.updateStep(1, 'success')
                    await this.runAutoActivation()
                    this.setState({ progressClosable: true, loading: false })
                    return
                }

                if (stepIndex === 1) {
                    await tranzila.stoCreate({ ...payload, token: verifyData.token })
                    this.updateStep(1, 'success')
                    await this.runAutoActivation()
                    this.setState({ progressClosable: true, loading: false })
                    return
                }
            }

            // ── IMMEDIATE_AND_RECURRING ─────────────────────────────────
            if (debitType === 'immediate_and_recurring') {
                if (stepIndex === 0) {
                    // Retry debit → then continue to stoCreate
                    const { data: newDebitData } = await tranzila.stoDebit({
                        ...payload,
                        terminal_name: 'mediwho1',
                        items: this.prepareItemsForSto(payload.items, payload.vat_percent)
                    })
                    this._retryDebitData = newDebitData
                    this.updateStep(0, 'success')

                    this.updateStep(1, 'loading')
                    await tranzila.stoCreate({
                        ...payload,
                        terminal_name: 'mediwho1tok',
                        token: newDebitData.token
                    })
                    this.updateStep(1, 'success')

                    await this.runAutoActivation()
                    this.setState({ progressClosable: true, loading: false })
                    this.startDocumentPolling()
                    return
                }

                if (stepIndex === 1) {
                    // Retry stoCreate only (debit already passed)
                    const debitData = this._retryDebitData
                    await tranzila.stoCreate({
                        ...payload,
                        terminal_name: 'mediwho1tok',
                        token: debitData.token
                    })
                    this.updateStep(1, 'success')

                    await this.runAutoActivation()
                    this.setState({ progressClosable: true, loading: false })
                    this.startDocumentPolling()
                    return
                }
            }
        } catch (error) {
            const res = error.response
            const errMsg =
                (res && res.data && (res.data.err_message || res.data.details || res.data.message)) || 'שגיאה בתהליך'
            // MASAV bank-detail codes (701-704): retrying with the same details reproduces them.
            const isBankDetailError = !!(res && res.data && res.data.err_key === 'BANK_DETAILS_INVALID')

            this.setState(prev => ({
                progressSteps: prev.progressSteps.map(s =>
                    s.status === 'loading'
                        ? {
                              ...s,
                              status: 'error',
                              canRetry: isBankDetailError ? false : !prev.retriedSteps.has(s.index)
                          }
                        : s
                ),
                progressError: errMsg,
                progressClosable: true,
                loading: false
            }))
            if (isBankDetailError && this._retryStoData && this._retryStoData.sto_id) {
                this.deactivateOrphanSto()
            }
        } finally {
            this._submissionInProgress = false
        }
    }

    closeProgressModal = () => {
        const chargeStepFailed = this.state.progressSteps.some(
            s => s.status === 'error' && (s.label || '').startsWith('ביצוע חיוב מס״ב')
        )
        const orphanRisk =
            this._orphanStoLive ||
            (chargeStepFailed && !this._orphanCleanupAttempted && this._retryStoData && this._retryStoData.sto_id)
        if (orphanRisk) {
            Modal.confirm({
                title: 'הוראת קבע פעילה ללא חיוב',
                content:
                    'הוראת הקבע נוצרה אך החיוב נכשל. אם לא כובתה — היא תחייב את הלקוח. יש לבדוק ולכבות בעמוד ניהול הוראות קבע.',
                okText: 'הבנתי, סגור',
                cancelText: 'חזור',
                onOk: () => this._finishCloseProgress()
            })
            return
        }
        this._finishCloseProgress()
    }

    _finishCloseProgress = () => {
        const allDone = this.state.progressSteps.every(
            s => s.status === 'success' || s.status === 'warning'
        )
        this.clearDocTimers()
        this._retryPayload = null
        this._retryVerifyData = null
        this._retryDebitType = ''
        this._retryStoItems = null
        this._retryChargeItems = null
        this._retryStoData = null
        this._retryChargeData = null
        this._retryDocumentUrl = null
        this._retryDebitData = null
        this._activationAttempted = false
        this._orphanCleanupAttempted = false
        this._orphanStoLive = false
        this.setState({
            progressVisible: false,
            progressSteps: [],
            progressError: '',
            progressClosable: false,
            retriedSteps: new Set()
        })
        if (allDone) {
            this.resetAll()
        }
    }

    // ── validators ───────────────────────────────────────────
    validateItems = () => {
        const { items } = this.state
        if (!items || !items.length) {
            return { valid: false, message: 'יש להוסיף לפחות פריט אחד' }
        }

        const { getFieldValue } = this.props.form
        let hasRealItem = false
        for (let i = 0; i < items.length; i++) {
            const unitPrice = getFieldValue(`items[${i}].unit_price`)
            const code = getFieldValue(`items[${i}].code`)
            const name = getFieldValue(`items[${i}].name`)

            if (EXTRA_CHARGE_SKUS.includes(code)) continue

            hasRealItem = true
            if (!unitPrice && unitPrice !== 0) {
                return { valid: false, message: `פריט ${i + 1}: יש להזין מחיר` }
            }
            if (!code) {
                return { valid: false, message: `פריט ${i + 1}: יש להזין קוד` }
            }
            if (!name) {
                return { valid: false, message: `פריט ${i + 1}: יש להזין שם` }
            }
        }

        if (!hasRealItem) {
            return { valid: false, message: 'יש להוסיף לפחות פריט אחד' }
        }

        return { valid: true, message: '' }
    }

    // ── field handlers ───────────────────────────────────────
    resetKeepingClientInfo = (newTerminalType: string, openSearch: boolean) => {
        const { form } = this.props
        const { items, existActiveOrder } = this.state

        const clientSnapshot: Record<string, any> = {}
        CLIENT_FORM_FIELDS.forEach(f => {
            clientSnapshot[f] = form.getFieldValue(f)
        })

        // Drop extra-charge rows from the snapshot when leaving bank mode —
        // they are auto-managed and should not carry over to credit mode.
        const leavingBankMode = this.state.terminalType === 'mediwhobk' && newTerminalType !== 'mediwhobk'
        const keptItems = leavingBankMode
            ? items.filter((_, i) => !EXTRA_CHARGE_SKUS.includes(form.getFieldValue(`items[${i}].code`)))
            : items

        // Read field values using original indices (before the filter shifts them)
        const itemsSnapshot = keptItems.map(key => {
            const i = items.indexOf(key)
            const vals: Record<string, any> = {}
            ITEM_FORM_FIELDS.forEach(f => {
                vals[f] = form.getFieldValue(`items[${i}].${f}`)
            })
            return vals
        })

        this.setState(
            {
                ...initialState,
                terminalType: newTerminalType,
                searchVisible: openSearch,
                existActiveOrder,
                items: keptItems
            },
            () => {
                form.resetFields()
                form.setFieldsValue(clientSnapshot)
                itemsSnapshot.forEach((vals, i) => {
                    const fieldValues: Record<string, any> = {}
                    ITEM_FORM_FIELDS.forEach(f => {
                        fieldValues[`items[${i}].${f}`] = vals[f]
                    })
                    form.setFieldsValue(fieldValues)
                })
                if (newTerminalType === 'mediwhobk') {
                    this.setState({}, () => this.syncExtraChargeItems())
                }
            }
        )
    }

    handleTerminalChange = e => {
        const newTerminalType = e.target.value
        const { getFieldValue } = this.props.form

        const hasClientData = ['name', 'id', 'address', 'email', 'phone_number', 'client_id'].some(
            f => !!getFieldValue(f)
        )

        const fullReset = () =>
            this.setState({ ...initialState, terminalType: newTerminalType }, () => {
                this.props.form.resetFields()
                this.applyDateDefaults()
                if (newTerminalType === 'mediwhobk') {
                    this.setState({}, () => this.syncExtraChargeItems())
                }
            })

        if (!hasClientData) {
            fullReset()
            return
        }

        Modal.confirm({
            centered: true,
            okText: 'כן',
            cancelText: 'לא',
            style: { direction: 'rtl' },
            title: 'האם לנקות את הטופס?',
            onOk: fullReset,
            onCancel: () => this.resetKeepingClientInfo(newTerminalType, false)
        })
    }

    handleCurrencyChange = e => {
        this.setState({ currencySign: signCurrency[e] })
    }

    handleChargeDateChange = date => {
        if (!date) return
        const day = Math.min(date.date(), 28)
        this.props.form.setFieldsValue({ charge_dom: day })
    }

    handleChargeDomChange = day => {
        const current = this.props.form.getFieldValue('first_charge_date')
        this.props.form.setFieldsValue({ first_charge_date: this.dateFromDay(day, current) })
    }

    // ── items (פריטים) ───────────────────────────────────────
    syncVatToItems = (vatPercent: number) => {
        const { items } = this.state
        if (!items.length) return
        const fieldValues: Record<string, any> = {}
        items.forEach((_, i) => {
            fieldValues[`items[${i}].vat_percent`] = vatPercent
        })
        this.props.form.setFieldsValue(fieldValues)
    }

    addRow = () => {
        this.setState(
            prev => ({ items: [...prev.items, ++this._nextItemId] }),
            () => {
                this.syncVatToItems(this.props.form.getFieldValue('vat_percent'))
            }
        )
    }

    removeRow = key => {
        const { items } = this.state
        const { form } = this.props
        const removedIndex = items.indexOf(key)
        const fields = ITEM_FORM_FIELDS

        // Snapshot all values NOW, before setState triggers a re-render.
        // AntD v3 flushes unregistered field values from its store once a
        // component stops decorating them, so reading in the setState callback
        // is too late — the shifted rows will have already re-registered under
        // new index-based names and the old values will be gone.
        const snapshot = items.map((_, i) => {
            const vals: Record<string, any> = {}
            fields.forEach(f => {
                vals[f] = form.getFieldValue(`items[${i}].${f}`)
            })
            return vals
        })

        this.setState(
            prev => ({ items: prev.items.filter(item => item !== key) }),
            () => {
                const newLength = this.state.items.length
                const remaining = snapshot.filter((_, i) => i !== removedIndex)

                remaining.forEach((vals, i) => {
                    const shifted: Record<string, any> = {}
                    fields.forEach(f => {
                        shifted[`items[${i}].${f}`] = vals[f]
                    })
                    form.setFieldsValue(shifted)
                })

                form.resetFields(fields.map(f => `items[${newLength}].${f}`))
            }
        )
    }

    // ── sub modal ────────────────────────────────────────────
    openSubModal = (flag, index = 0) => {
        this.setState({ subModalVisible: flag, subItemIndex: index })
    }

    handleSubSelect = (subscription, done) => {
        const { subItemIndex } = this.state

        this.props.form.setFieldsValue({
            [`items[${subItemIndex}].unit_price`]: subscription.price,
            [`items[${subItemIndex}].code`]: subscription.sku,
            [`items[${subItemIndex}].name`]:
                subscription.sku === 'H-0010' || subscription.sku === 'H-0015'
                    ? subscription.name
                    : `${subscription.sku} - ${subscription.name}`
        })
        done()
    }

    // ── user search ──────────────────────────────────────────
    findUser = (values, done) => {
        const { setFieldsValue } = this.props.form
        tranzila
            .findUserDataForSto(values)
            .then(({ status, data }) => {
                if (status !== 200) {
                    this._foundUserSub = null
                    done(false, 'משתמש לא נמצא, הטופס לא מולא ❌')
                    return
                }

                this.populateUserFields(data, setFieldsValue)
                done(true, 'המשתמש נמצא, הטופס מולא ✅')
            })
            .catch(err => {
                console.error(err)
                this._foundUserSub = null
                done(false, stoFailureText(err, 'משתמש לא נמצא ❌'))
            })
    }

    populateUserFields = (data, setFieldsValue) => {
        this._foundUserSub = {
            client_id: data.client_id || null,
            user_sub_id: data.user_sub_id || null,
            user_sub_status: data.user_sub_status || null,
            coupon_details: data.coupon_details || null
        }

        const phoneAreaCode = data.phone_number ? data.phone_number.slice(0, 2) : ''
        const phoneNumber = data.phone_number ? data.phone_number.slice(2) : ''
        const isAccepted = localCodes['972'].find(({ label }) => label === phoneAreaCode)
        setFieldsValue({
            ...(data.client_full_name && { client_full_name: data.client_full_name }),
            ...(data.user_sub_id && { user_sub_id: data.user_sub_id }),
            ...(data.client_id && { client_id: data.client_id }),
            ...(data.name && { name: data.name }),
            ...(data.address && { address: data.address }),
            ...(data.email && { email: data.email }),
            ...(data.id && { id: data.id }),
            ...(isAccepted && { phone_number: phoneNumber }),
            ...(isAccepted && { phone_area_code: phoneAreaCode })
        })

        this.setState({ existActiveOrder: data.user_sub_sto_status === 'active', currencySign: signCurrency['ILS'] })

        // Reset PaymentMethod fields to defaults
        this.props.form.resetFields([
            'debit_type',
            'sto_payments_number',
            'charge_dom',
            'first_charge_date',
            'vat_percent',
            'charge_frequency',
            'currency_code',
            'charge_amount',
            'card_number',
            'cvv_code',
            'card_holder_name',
            'card_holder_id',
            'remarks',
            'bank_code',
            'account_number',
            'branch_code'
        ])
        this.applyDateDefaults()

        // debit_type just reset to its default — re-derive H-0010/H-0015 rows from the
        // (now-default) radio values, deferred one render like handleTerminalChange does.
        // Safe alongside the prefill below: replaceMainItemsKeepExtra preserves extra rows,
        // and in credit mode syncExtraChargeItems only strips fee SKUs.
        this.setState({}, () => this.syncExtraChargeItems())

        if (data.user_sub_price && data.subscription_sku) {
            const isBank = this.state.terminalType === 'mediwhobk'

            if (isBank) {
                this.replaceMainItemsKeepExtra({
                    unit_price: data.user_sub_price,
                    code: data.subscription_sku,
                    name: data.subscription_name
                })
            } else {
                // Credit mode — replace all items (existing behavior)
                const keys = [++this._nextItemId]
                this.setState({ items: keys }, () => {
                    this.props.form.setFieldsValue({
                        'items[0].unit_price': data.user_sub_price,
                        'items[0].code': data.subscription_sku,
                        'items[0].name': data.subscription_name,
                        'items[0].vat_percent': this.props.form.getFieldValue('vat_percent')
                    })
                })
            }
        } else if (this.state.terminalType === 'mediwhobk') {
            // User has NO subscription — in bank mode, remove only
            // non-extra-charge items (keep H-0010, H-0015)
            this.removeAllMainItems()
        } else {
            this.setState({ items: [] })
        }
    }

    replaceMainItemsKeepExtra = (newItem: { unit_price: number; code: string; name: string }) => {
        const { items } = this.state
        const { form } = this.props

        // Snapshot extra-charge items
        const extraSnapshots: Array<{ key: number; vals: Record<string, any> }> = []
        items.forEach((key, i) => {
            const code = form.getFieldValue(`items[${i}].code`)
            if (EXTRA_CHARGE_SKUS.includes(code)) {
                const vals: Record<string, any> = {}
                ITEM_FORM_FIELDS.forEach(f => {
                    vals[f] = form.getFieldValue(`items[${i}].${f}`)
                })
                extraSnapshots.push({ key, vals })
            }
        })

        // New items array: new user item + preserved extras
        const newUserKey = ++this._nextItemId
        const newItems = [newUserKey, ...extraSnapshots.map(s => s.key)]

        this.setState({ items: newItems }, () => {
            // Set new user item at index 0
            form.setFieldsValue({
                'items[0].unit_price': newItem.unit_price,
                'items[0].code': newItem.code,
                'items[0].name': newItem.name,
                'items[0].vat_percent': form.getFieldValue('vat_percent')
            })

            // Restore extras at indices after the user item
            extraSnapshots.forEach((snapshot, i) => {
                const newIndex = i + 1
                const fieldValues: Record<string, any> = {}
                ITEM_FORM_FIELDS.forEach(f => {
                    fieldValues[`items[${newIndex}].${f}`] = snapshot.vals[f]
                })
                form.setFieldsValue(fieldValues)
            })
        })
    }

    removeAllMainItems = () => {
        const { items } = this.state
        const { form } = this.props

        // Snapshot extra-charge items
        const extraSnapshots: Array<{ key: number; vals: Record<string, any> }> = []
        items.forEach((key, i) => {
            const code = form.getFieldValue(`items[${i}].code`)
            if (EXTRA_CHARGE_SKUS.includes(code)) {
                const vals: Record<string, any> = {}
                ITEM_FORM_FIELDS.forEach(f => {
                    vals[f] = form.getFieldValue(`items[${i}].${f}`)
                })
                extraSnapshots.push({ key, vals })
            }
        })

        const newItems = extraSnapshots.map(s => s.key)

        this.setState({ items: newItems }, () => {
            // Reset only item fields for old length
            const oldLength = items.length
            const allItemFields: string[] = []
            for (let i = 0; i < oldLength; i++) {
                ITEM_FORM_FIELDS.forEach(f => {
                    allItemFields.push(`items[${i}].${f}`)
                })
            }
            form.resetFields(allItemFields)

            // Restore extras at new indices
            extraSnapshots.forEach((snapshot, i) => {
                const fieldValues: Record<string, any> = {}
                ITEM_FORM_FIELDS.forEach(f => {
                    fieldValues[`items[${i}].${f}`] = snapshot.vals[f]
                })
                form.setFieldsValue(fieldValues)
            })
        })
    }

    // ── modal close ──────────────────────────────────────────
    closeModal = () => {
        if (this._submissionInProgress || this.state.loading) return
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur()
        }
        this.resetAll()
        this.props.onClose()
    }

    // ── computed ─────────────────────────────────────────────
    getCurrentFormItems = () => {
        const { items } = this.state
        const { getFieldValue } = this.props.form

        return (items || []).map((_, index) => {
            const item: Record<string, any> = {}
            ITEM_FORM_FIELDS.forEach(field => {
                item[field] = getFieldValue(`items[${index}].${field}`)
            })
            return item
        })
    }

    getItemsTotalSum = () => {
        const formItems = this.getCurrentFormItems()
        if (!formItems.length) return null

        const { getFieldValue } = this.props.form
        const itemsForTotal =
            this.state.terminalType === 'mediwhobk'
                ? this.prepareBankItems(formItems, getFieldValue('debit_type'), {
                      first_month_charge: getFieldValue('first_month_charge'),
                      recurring_charge: getFieldValue('recurring_charge')
                  }).chargeItems
                : formItems

        return itemsForTotal.reduce((sum, item) => {
            const price = item.unit_price || 0
            const quantity = item.units_number || 1
            return sum + price * quantity
        }, 0)
    }

    handleDebitTypeChange = e => {
        const value = e.target.value

        if (this.state.terminalType === 'mediwho1tok' || this.state.terminalType === 'mediwho1') {
            this.setState({ terminalType: value === 'immediate_only' ? 'mediwho1' : 'mediwho1tok' })
        }

        this.props.form.setFieldsValue({
            sto_payments_number: value === 'immediate_only' ? 1 : 9999
        })
        // Sub-section fields (first_month_charge / recurring_charge / extra_charge)
        // only re-register their initialValue after the sub-sections mount/unmount on
        // re-render.  The setState wrapper ensures that render has happened before we
        // read those fields.  We still pass debit_type as an explicit override so we
        // never rely on getFieldValue returning the new value before that re-render.
        this.setState({}, () => this.syncExtraChargeItems({ debit_type: value }))
    }

    handleBankSubRadioChange = (overrides: Record<string, string>) => {
        // The triggering field's value is already committed to AntD's store by the
        // time onChange fires, but we pass it as an explicit override anyway to make
        // the sync independent of any internal AntD flush ordering.
        this.syncExtraChargeItems(overrides)
    }

    // ── extra charge item management ─────────────────────────
    findItemIndexBySku = (sku: string): number =>
        this.state.items.findIndex((_, i) => this.props.form.getFieldValue(`items[${i}].code`) === sku)

    addExtraChargeRow = (item: { name: string; sku: string; price: number }) => {
        const newKey = ++this._nextItemId
        this.setState(
            prev => ({ items: [...prev.items, newKey] }),
            () => {
                const newIndex = this.state.items.indexOf(newKey)
                if (newIndex === -1) return
                this.props.form.setFieldsValue({
                    [`items[${newIndex}].unit_price`]: item.price,
                    [`items[${newIndex}].code`]: item.sku,
                    [`items[${newIndex}].name`]: item.name,
                    [`items[${newIndex}].vat_percent`]: this.props.form.getFieldValue('vat_percent')
                })
            }
        )
    }

    removeItemBySku = (sku: string) => {
        const index = this.findItemIndexBySku(sku)
        if (index === -1) return
        this.removeRow(this.state.items[index])
    }

    syncExtraChargeItems = (
        overrides: {
            debit_type?: string
            first_month_charge?: string
            recurring_charge?: string
            extra_charge?: string
        } = {}
    ) => {
        if (this.state.terminalType !== 'mediwhobk') {
            EXTRA_CHARGE_SKUS.forEach(sku => this.removeItemBySku(sku))
            return
        }

        const { form } = this.props
        const debitType = overrides.debit_type !== undefined ? overrides.debit_type : form.getFieldValue('debit_type')
        const firstMonthCharge =
            overrides.first_month_charge !== undefined
                ? overrides.first_month_charge
                : form.getFieldValue('first_month_charge')
        const recurringCharge =
            overrides.recurring_charge !== undefined
                ? overrides.recurring_charge
                : form.getFieldValue('recurring_charge')
        const extraCharge =
            overrides.extra_charge !== undefined ? overrides.extra_charge : form.getFieldValue('extra_charge')

        let needsSetupFee = false
        let needsClearingFee = false

        if (debitType === 'recurring_only') {
            needsClearingFee = extraCharge === 'clearing_fee'
        } else if (debitType === 'immediate_and_recurring') {
            needsSetupFee = firstMonthCharge === 'setup_fee'
            needsClearingFee = recurringCharge === 'clearing_fee' || firstMonthCharge === 'clearing_fee'
        }

        const hasSetupFee = this.findItemIndexBySku('H-0010') !== -1
        const clearingFeeIndex = this.findItemIndexBySku('H-0015')
        const hasClearingFee = clearingFeeIndex !== -1
        const clearingFeeName = getClearingFeeItemName(debitType, firstMonthCharge, recurringCharge)

        if (needsClearingFee && hasClearingFee) {
            form.setFieldsValue({ [`items[${clearingFeeIndex}].name`]: clearingFeeName })
        }

        // Removals before additions so index-shifting in removeRow stays predictable
        if (!needsSetupFee && hasSetupFee) this.removeItemBySku('H-0010')
        if (!needsClearingFee && hasClearingFee) this.removeItemBySku('H-0015')
        if (needsSetupFee && !hasSetupFee) this.addExtraChargeRow(SETUP_FEE_ITEM)
        if (needsClearingFee && !hasClearingFee) {
            this.addExtraChargeRow({ ...CLEARING_FEE_ITEM, name: clearingFeeName })
        }
    }

    setFieldValue = (key, value) => {
        this.props.form.setFieldsValue({ [key]: value })
    }

    // ── render ───────────────────────────────────────────────
    render() {
        const { roles } = this.props.authStore
        const { getFieldDecorator, getFieldValue } = this.props.form
        const { visible } = this.props
        const {
            loading,
            currencySign,
            items,
            terminalType,
            existActiveOrder,
            searchVisible,
            subModalVisible,
            progressSteps,
            progressVisible,
            progressError,
            progressClosable,
            documentUrl
        } = this.state

        const hasAcceptedRole = roles.includes('admin') || roles.includes('money')
        const isAdmin = roles.includes('admin')
        const internationalCode = getFieldValue('phone_country_code')
        const clientId = getFieldValue('client_id')
        const itemsTotalSum = this.getItemsTotalSum()

        return (
            <Modal
                width={'98vw'}
                style={{ top: 10 }}
                onCancel={this.closeModal}
                closeIcon={
                    <span className={xIcon} style={{ display: loading ? 'none' : 'inline-block' }}>
                        ×
                    </span>
                }
                visible={visible}
                forceRender
                maskClosable={false}
                title="הוספת הוראת חיוב"
                footer={null}
            >
                <SearchSubModal
                    visible={subModalVisible && visible}
                    onClose={() => this.openSubModal(false)}
                    onSelect={this.handleSubSelect}
                    terminalType={terminalType}
                />
                <SearchUserModal
                    visible={searchVisible && visible}
                    onClose={() => this.setState({ searchVisible: false })}
                    onSearch={this.findUser}
                    clientId={clientId}
                    mainClose={() => this.closeModal()}
                />
                <TransactionProgressModal
                    visible={progressVisible}
                    steps={progressSteps}
                    errorMessage={progressError}
                    closable={progressClosable}
                    onClose={this.closeProgressModal}
                    onRetry={this.handleRetryStep}
                    documentUrl={documentUrl}
                />

                <FormItem className={formMain}>
                    <div className={formInner} style={{ visibility: hasAcceptedRole ? 'visible' : 'hidden' }}>
                        <div className={formTitle}>סוג מסוף:</div>
                        <Radio.Group
                            value={terminalType}
                            onChange={!hasAcceptedRole ? () => {} : this.handleTerminalChange}
                            buttonStyle="solid"
                        >
                            <Radio.Button value="mediwho1tok">אשראי</Radio.Button>
                            <Radio.Button value="mediwhobk">הו״ק בנקאית</Radio.Button>
                        </Radio.Group>
                    </div>
                    <Tooltip placement="top" title="איפוס טופס">
                        <Icon type="sync" className={refreshFormIcon} onClick={this.resetAll} />
                    </Tooltip>
                </FormItem>

                <Form onSubmit={this.handleSubmit} layout="vertical" className={formPadding}>
                    <FormContext.Provider
                        value={{ getFieldDecorator, getFieldValue, setFieldValue: this.setFieldValue }}
                    >
                        <Row gutter={24}>
                            <Col span={6}>
                                <PaymentMethod
                                    terminalType={terminalType}
                                    onDebitTypeChange={this.handleDebitTypeChange}
                                    onBankSubRadioChange={this.handleBankSubRadioChange}
                                    roles={roles}
                                />
                            </Col>
                            <Col span={6}>
                                <PaymentInfo
                                    currencySign={currencySign}
                                    items={items}
                                    terminalType={terminalType}
                                    itemsTotalSum={itemsTotalSum}
                                    defaultChargeDay={this.getDefaultChargeDay()}
                                    defaultChargeDate={this.getDefaultChargeDate()}
                                    onCurrencyChange={this.handleCurrencyChange}
                                    onChargeDateChange={this.handleChargeDateChange}
                                    onChargeDomChange={this.handleChargeDomChange}
                                    onVatPercentChange={this.syncVatToItems}
                                />
                            </Col>
                            <Col span={12}>
                                <div
                                    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 5 }}
                                >
                                    <div className={columnTitle}>פרטי לקוח</div>
                                    <Tooltip placement="top" title="חיפוש לקוח">
                                        <Icon
                                            type="search"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => this.setState({ searchVisible: true })}
                                        />
                                    </Tooltip>
                                </div>
                                <ClientInfo
                                    internationalCode={internationalCode}
                                    items={items}
                                    existActiveOrder={existActiveOrder}
                                    addRow={this.addRow}
                                    removeRow={this.removeRow}
                                    openSubModal={this.openSubModal}
                                    terminalType={terminalType}
                                />
                            </Col>
                        </Row>

                        <Row gutter={24} justify="end" type="flex">
                            <Button type="primary" htmlType="submit" disabled={!hasAcceptedRole || loading}>
                                {loading ? <Icon type="loading" /> : <span>שלח</span>}
                            </Button>
                        </Row>
                    </FormContext.Provider>
                </Form>
            </Modal>
        )
    }
}

export default Form.create()(CreateBankSto)
